import React, { useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { Close, InfoOutlined } from "@mui/icons-material";
import { Alert, Container, Dialog, IconButton, SwipeableDrawer } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import useHelper from "@/src/_common/useHelper";

/** API */
import { getCertificateFromAPI } from "@/src/_APIs";

/** UI */
const AppTooltip = dynamic(() => import("@/src/_common/AppTooltip"));
const Preloader = dynamic(() => import("@/src/_common/Preloader"));
const CertificateContent = dynamic(() => import("./CertificateContent"));
const CertificateAccordion = dynamic(() => import("./CertificateAccordion"));

const Target = (props) => {
    const { target, open, onClose, onOpen, classes, from } = props;
    return target === "dialog" ? (
        <Dialog
            open={open}
            onClose={(event) => onClose({ event, show: false })}
            classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
            fullWidth
            maxWidth={"xl"}
        >
            {props.children}
        </Dialog>
    ) : (
        <SwipeableDrawer
            anchor={"right"}
            open={!!open}
            onClose={(event) => onClose({ event, show: false })}
            onOpen={(event) => onOpen({ event, show: true })}
            classes={{ root: classes.drawerRoot, paper: classes.drawerPaper }}
            slotProps={{
                backdrop: {
                    classes: { root: clsx(from === "hvd" ? classes.backdropRootLight : classes.backdropRoot) },
                },
            }}
            // hideBackdrop
        >
            {props.children}
        </SwipeableDrawer>
    );
};

const useStyles = makeStyles((theme) => ({
    drawerRoot: {
        zIndex: 2101,
    },
    drawerPaper: {
        // padding: theme.spacing(0),
        borderRadius: "20px 0 0 20px",
        backgroundColor: "transparent",
        boxShadow: "none",
        paddingRight: 0,
        maxHeight: "100%",
        // top: "51%",
        // transform: "translateY(-51%)!important",
        top: 0,
    },
    dialogRoot: {
        zIndex: 2101,
    },
    backdropRoot: {
        opacity: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
    },
    backdropRootLight: {
        opacity: 1,
        backgroundColor: "rgba(0,0,0,0.05)",
    },
    dialogPaper: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(0),
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none",
        maxHeight: "100%",
        [theme.breakpoints.down("lg")]: {
            margin: theme.spacing(1),
            width: "calc(100% - 16px)",
            height: "calc(100% - 16px)",
            padding: theme.spacing(0),
        },
    },
    iconBtn: {
        // marginTop: -3,
        // marginLeft: 2,
        margin: theme.spacing(-0.25, 0, -0.25, 0.25),
        padding: 5,
    },
    rightIcon: {
        fontSize: "1.1111111rem",
    },
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(2, 2, 2, 2),
        minWidth: "50vw",
        display: "flex",
        flexGrow: 1,
        flexWrap: "wrap",
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(1, 1, 2, 1),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1, 0.5, 2, 0.5),
        },
    },
    boxedWrapperDrawer: {
        padding: theme.spacing(3, 1, 2, 4),
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        maxWidth: "210mm",
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(2, 0.5, 1, 0.5),
        },
    },
}));

const Certificate = (props) => {
    const { color = "secondary", hideIcon, onClose, indicatorId, item, show, target = "drawer", from } = props;

    const { t } = useTranslation();
    const { translationFromJson } = useHelper();
    const classes = useStyles();

    const [LOADING, setLOADING] = useState(false);
    const [ERROR, setERROR] = useState(false);
    const [DATA, setDATA] = useState([]);

    useEffect(() => {
        if (show) {
            setLOADING(true);
            setERROR(false);
            getCertificateFromAPI(indicatorId || item.indicator_id)
                .then((result) => {
                    if (result.message && result.message.type === "Error") {
                        setERROR(result);
                    } else {
                        setDATA(result);
                    }
                })
                .catch((error) => {})
                .finally(() => {
                    setLOADING(false);
                });
        }
    }, [show]);

    const toggleCertificate = ({ event, show }) => {
        if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
            return;
        }
        onClose(show);
    };

    return (
        <React.Fragment>
            {hideIcon ? (
                ""
            ) : (
                <AppTooltip
                    title={t("Otwórz metadane, w tym definicje")}
                    arrow
                    placement={"top"}
                    type={"dark"}
                    classes={{ tooltip: classes.tooltip }}
                >
                    <IconButton
                        size={"small"}
                        onClick={(event) => toggleCertificate({ event, show: true })}
                        className={classes.iconBtn}
                        aria-label={t("Otwórz metadane dla zmiennej")}
                    >
                        <InfoOutlined fontSize={"small"} className={classes.rightIcon} color={"primary"} />
                    </IconButton>
                </AppTooltip>
            )}
            <Target
                anchor={"right"}
                open={show}
                onClose={(event) => toggleCertificate({ event, show: false })}
                onOpen={(event) => toggleCertificate({ event, show: true })}
                target={target}
                classes={classes}
                from={from}
            >
                <>
                    <Container
                        maxWidth={"lg"}
                        className={clsx(classes.boxedWrapper, target === "drawer" && classes.boxedWrapperDrawer)}
                    >
                        {!!LOADING ? (
                            <>
                                <Preloader type={"linear"} />
                            </>
                        ) : !!ERROR ? (
                            <>
                                <Alert
                                    severity={"error"}
                                    sx={{ width: "100%", alignSelf: "flex-start", justifyContent: "space-between" }}
                                    variant={"outlined"}
                                    action={
                                        <IconButton
                                            size={"small"}
                                            onClick={(event) =>
                                                toggleCertificate({
                                                    event,
                                                    show: false,
                                                })
                                            }
                                        >
                                            <Close fontSize={"small"} />
                                        </IconButton>
                                    }
                                >
                                    <span>{translationFromJson(ERROR.message, "text") || t("Nieznany błąd")}</span>
                                </Alert>
                            </>
                        ) : (
                            <>
                                {!!DATA?.length && DATA.length === 1 ? (
                                    <CertificateContent
                                        data={DATA[0]}
                                        color={color}
                                        onClose={(event) => toggleCertificate({ event, show: false })}
                                        target={target}
                                    />
                                ) : (
                                    <CertificateAccordion
                                        data={DATA}
                                        color={color}
                                        onClose={(event) => toggleCertificate({ event, show: false })}
                                        target={target}
                                    />
                                )}
                            </>
                        )}
                    </Container>
                </>
            </Target>
        </React.Fragment>
    );
};

export default Certificate;
